import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useUser } from "../../../hooks/useUser";
import { useResto } from "../../../hooks/useResto";
import { buildQuizHeaders } from "../utils/buildQuizHeaders";

import DesktopPageHeader from "../../../components/DesktopPageHeader";
import { useDeviceType } from "../../../hooks/useMediaQuery";
import Modal from "../../../components/Modal";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { getTenantId } from "../../../utils/tenant";
import { useTenantTopicsAccess } from "../hooks/useTenantTopicsAccess";
import { addTenantTopic } from "../services/tenantTopicsService";
import { getTriviaMarketplacePacks } from "../services/triviaMarketplaceService";
import { useOwnerRestaurantSelection } from "../../../hooks/useOwnerRestaurantSelection";
import {
  QuizBadge,
  QuizCardSkeletonGroup,
  QuizEmptyMessage,
  QuizErrorMessage,
  QuizInlineInfo,
  QuizListActions,
  QuizPrimaryButton,
  QuizProfileWrapper,
  QuizSectionTitle,
  QuizSummaryCard,
  QuizSummaryGrid,
  QuizTabButton,
  QuizTabs,
  QuizTopicCard,
  QuizTopicHeader,
} from "../components/QuizProfileLayout";

const AVAILABLE_TOPICS_PAGE_SIZE = 5;

const getTopicId = (topic) => topic?._id || topic?.id || topic?.topic_id || "";

const getPageSlice = (items = [], currentPage = 1, pageSize = AVAILABLE_TOPICS_PAGE_SIZE) => {
  const start = (currentPage - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

function TopicsPagination({
  currentPage,
  totalItems,
  pageSize = AVAILABLE_TOPICS_PAGE_SIZE,
  onPageChange,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationContainer>
      <PaginationInfo>
        Mostrando {startItem}-{endItem} de {totalItems}
      </PaginationInfo>
      <PaginationControls>
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ‹
        </PaginationButton>
        {getVisiblePages(currentPage, totalPages).map((page, index) =>
          page === "..." ? (
            <PaginationInfo key={`ellipsis-${index}`}>...</PaginationInfo>
          ) : (
            <PaginationButton
              key={page}
              type="button"
              active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationButton>
          )
        )}
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </PaginationButton>
      </PaginationControls>
    </PaginationContainer>
  );
}

const QuizAdminHome = ({ headerContent = null }) => {
  const { isResto, isCEO, isOwner, userId } = useUser();
  const { resto, getResto } = useResto();
  const { selectedOwnerRestaurant } = useOwnerRestaurantSelection();
  const { isDesktop } = useDeviceType();
  const [activeTab, setActiveTab] = useState("available");
  const [restoName, setRestoName] = useState("");
  const [restoAddress, setRestoAddress] = useState("");
  const [avatar, setAvatar] = useState("");
  const [restoId, setRestoId] = useState("");
  const [catalogTopics, setCatalogTopics] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [topicActionLoading, setTopicActionLoading] = useState("");
  const [topicActionError, setTopicActionError] = useState("");
  const [topicActionSuccess, setTopicActionSuccess] = useState("");
  const [pendingRedeemTopic, setPendingRedeemTopic] = useState(null);
  const [availableTopicsPage, setAvailableTopicsPage] = useState(1);

  const headers = useMemo(() => {
    const storedName = typeof window !== "undefined" ? localStorage.getItem("hname") : "";
    const resolvedRestoName = isOwner
      ? selectedOwnerRestaurant?.name || resto?.name || storedName || undefined
      : resto?.name || storedName || undefined;
    return buildQuizHeaders({
      userId: userId || undefined,
      userName: resolvedRestoName,
    });
  }, [isOwner, selectedOwnerRestaurant?.name, resto?.name, userId]);
  const {
    topicIds: allowedTopicIds,
    loading: loadingTopicAccess,
    error: topicAccessError,
    reload: reloadTopicAccess,
  } = useTenantTopicsAccess({ enabled: Boolean(isResto || isCEO || isOwner) });

  const loadCatalogTopics = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const topics = await getTriviaMarketplacePacks({ headers });
      setCatalogTopics(topics);
    } catch (err) {
      setCatalogError(err?.message || "No se pudo cargar el catálogo de tópicos.");
      setCatalogTopics([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadCatalogTopics();
  }, [loadCatalogTopics]);

  useEffect(() => {
    getResto();
  }, [getResto]);

  useEffect(() => {
    if (resto) {
      setRestoId(resto?._id || "");
      setRestoName(resto?.name || "");
      setRestoAddress(resto?.address || "");
      setAvatar(resto?.brand || "");
    }
  }, [resto]);

  useEffect(() => {
    if (!isOwner || !selectedOwnerRestaurant) return;

    setRestoId(String(selectedOwnerRestaurant?._id || selectedOwnerRestaurant?.id || ""));
    setRestoName(selectedOwnerRestaurant?.name || "");
    setRestoAddress(selectedOwnerRestaurant?.address || "");
    setAvatar(selectedOwnerRestaurant?.brand || "");
  }, [isOwner, selectedOwnerRestaurant]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const quizTab = params.get("quizTab");
    if (quizTab === "available" || quizTab === "redeemed") {
      setActiveTab(quizTab);
    }
  }, []);

  const activeTopicIds = Array.isArray(allowedTopicIds) ? allowedTopicIds : [];
  const activeTopicSet = useMemo(() => new Set(activeTopicIds), [activeTopicIds]);
  const remainingIncluded = Number.POSITIVE_INFINITY;
  const isAvailableTopicsLoading = catalogLoading || loadingTopicAccess;
  const activeTopics = useMemo(
    () => catalogTopics.filter((topic) => activeTopicSet.has(getTopicId(topic))),
    [catalogTopics, activeTopicSet]
  );
  const availableTopics = useMemo(
    () => catalogTopics.filter((topic) => !activeTopicSet.has(getTopicId(topic))),
    [catalogTopics, activeTopicSet]
  );
  const paginatedAvailableTopics = useMemo(
    () => getPageSlice(availableTopics, availableTopicsPage, AVAILABLE_TOPICS_PAGE_SIZE),
    [availableTopics, availableTopicsPage]
  );

  useEffect(() => {
    setAvailableTopicsPage(1);
  }, [availableTopics.length, activeTab]);

  const handleActivateIncluded = async (topic) => {
    const topicId = getTopicId(topic);
    const tenantId = getTenantId();
    if (!topicId || !tenantId || remainingIncluded <= 0) return;

    setTopicActionLoading(topicId);
    setTopicActionError("");
    setTopicActionSuccess("");
    try {
      await addTenantTopic(tenantId, topicId);
      await reloadTopicAccess();
      setTopicActionSuccess(`Tópico canjeado: ${topic?.name || "sin nombre"}.`);
    } catch (err) {
      setTopicActionError(err?.message || "No se pudo activar el tópico.");
    } finally {
      setTopicActionLoading("");
    }
  };

  const handleOpenRedeemModal = (topic) => {
    if (remainingIncluded <= 0) return;
    setPendingRedeemTopic(topic || null);
  };

  const handleCloseRedeemModal = () => {
    setPendingRedeemTopic(null);
  };

  const handleConfirmRedeem = async () => {
    if (!pendingRedeemTopic || topicActionLoading || remainingIncluded <= 0) return;
    await handleActivateIncluded(pendingRedeemTopic);
    setPendingRedeemTopic(null);
  };

  const content = !isResto && !isCEO && !isOwner ? (
    <Page>
      <QuizProfileWrapper />
    </Page>
  ) : (
    <Page>
      <QuizProfileWrapper>
        {[topicAccessError, catalogError, topicActionError].filter(Boolean).map((message, index) => (
          <QuizErrorMessage key={`${message}-${index}`}>⚠️ {message}</QuizErrorMessage>
        ))}

        <QuizTabs>
          <QuizTabButton type="button" $active={activeTab === "available"} onClick={() => setActiveTab("available")}>
            Disponibles
          </QuizTabButton>
          <QuizTabButton type="button" $active={activeTab === "redeemed"} onClick={() => setActiveTab("redeemed")}>
            Activados
          </QuizTabButton>
        </QuizTabs>

        {topicActionSuccess && <QuizInlineInfo>{topicActionSuccess}</QuizInlineInfo>}

        {activeTab === "available" && (
          <>
            <QuizSectionTitle>Topicos disponibles</QuizSectionTitle>
            {isAvailableTopicsLoading && (
              <QuizCardSkeletonGroup>
                <SkeletonLoader active width="100%" height="90px" borderRadius="12px" />
                <SkeletonLoader active width="100%" height="90px" borderRadius="12px" />
                <SkeletonLoader active width="100%" height="90px" borderRadius="12px" />
              </QuizCardSkeletonGroup>
            )}
            {!isAvailableTopicsLoading && availableTopics.length === 0 && (
              <QuizEmptyMessage>No hay tópicos adicionales para activar.</QuizEmptyMessage>
            )}
            {!isAvailableTopicsLoading &&
              paginatedAvailableTopics.map((topic) => {
                const topicId = getTopicId(topic);
                const isLoadingAction = topicActionLoading === topicId;
                const canRedeem = remainingIncluded > 0;
                return (
                  <QuizTopicCard key={topicId}>
                    <QuizTopicHeader>
                      <strong>{topic.name || "Topico"}</strong>
                      <p>{topic.description || "Sin descripción"}</p>
                    </QuizTopicHeader>
                    <QuizSummaryGrid>
                      <QuizSummaryCard>
                        <strong>Estado</strong>
                        <span>Disponible</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Tipo</strong>
                        <span>{topic.is_global ? "Global" : "Local"}</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Preguntas</strong>
                        <span>{topic.total_questions || "-"}</span>
                      </QuizSummaryCard>
                    </QuizSummaryGrid>
                    <QuizListActions>
                      {topic.is_global && <QuizBadge>Global</QuizBadge>}
                      <QuizPrimaryButton
                        type="button"
                        onClick={() => handleOpenRedeemModal(topic)}
                        disabled={Boolean(topicActionLoading) || !canRedeem}
                      >
                        {isLoadingAction ? "Activando..." : "Activar"}
                      </QuizPrimaryButton>
                    </QuizListActions>
                  </QuizTopicCard>
                );
              })}
            {!isAvailableTopicsLoading && (
              <TopicsPagination
                currentPage={availableTopicsPage}
                totalItems={availableTopics.length}
                pageSize={AVAILABLE_TOPICS_PAGE_SIZE}
                onPageChange={setAvailableTopicsPage}
              />
            )}
          </>
        )}

        {activeTab === "redeemed" && (
          <>
            <QuizSectionTitle>Topicos activados</QuizSectionTitle>
            {(catalogLoading || loadingTopicAccess) && (
              <QuizCardSkeletonGroup>
                <SkeletonLoader active width="100%" height="90px" borderRadius="12px" />
                <SkeletonLoader active width="100%" height="90px" borderRadius="12px" />
              </QuizCardSkeletonGroup>
            )}
            {!catalogLoading && !loadingTopicAccess && activeTopics.length === 0 && (
              <QuizEmptyMessage>No tienes tópicos activados.</QuizEmptyMessage>
            )}
            {!catalogLoading &&
              !loadingTopicAccess &&
              activeTopics.map((topic) => (
                <QuizTopicCard key={getTopicId(topic)}>
                  <QuizTopicHeader>
                    <strong>{topic.name || "Topico"}</strong>
                    <p>{topic.description || "Sin descripción"}</p>
                  </QuizTopicHeader>
                  <QuizSummaryGrid>
                    <QuizSummaryCard>
                      <strong>Estado</strong>
                      <span>Activado</span>
                    </QuizSummaryCard>
                    <QuizSummaryCard>
                      <strong>Tipo</strong>
                      <span>{topic.is_global ? "Global" : "Local"}</span>
                    </QuizSummaryCard>
                    <QuizSummaryCard>
                      <strong>Preguntas</strong>
                      <span>{topic.total_questions || "-"}</span>
                    </QuizSummaryCard>
                  </QuizSummaryGrid>
                  <QuizBadge>Activado</QuizBadge>
                </QuizTopicCard>
              ))}
          </>
        )}

        <Modal
          isOpen={Boolean(pendingRedeemTopic)}
          onClose={handleCloseRedeemModal}
          label="Confirmar activación"
          acceptButtonText={topicActionLoading ? "Activando..." : "Activar"}
          cancelButtonText="Cancelar"
          onAccept={handleConfirmRedeem}
          onCancel={handleCloseRedeemModal}
        >
          <p style={{ margin: 0, color: "#334155", fontSize: "16px", lineHeight: 1.5 }}>
            ¿Seguro que deseas activar este tópico?
          </p>
          <p style={{ margin: "10px 0 0", color: "#475569", fontSize: "16px" }}>
            {pendingRedeemTopic?.name || "Tópico"}.
          </p>
          <p style={{ margin: "10px 0 0", color: "#0f172a", fontSize: "16px", fontWeight: 600 }}>
            Te quedarán {Number.isFinite(remainingIncluded) ? Math.max(0, remainingIncluded - 1) : "ilimitados"} para activar.
          </p>
        </Modal>

      </QuizProfileWrapper>
    </Page>
  );

  if (!isDesktop) {
    return content;
  }

  return (
    <DesktopContainer>
      <DesktopPageHeader img={avatar} alt={restoName} />
      {headerContent}
      {content}
    </DesktopContainer>
  );
};

export default QuizAdminHome;

const DesktopContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Page = styled.section`
  min-height: calc(100vh - 140px);
  padding: 20px 0 90px;
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0 4px;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #777;
  text-align: center;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: ${({ disabled, active }) => (active ? "#111" : disabled ? "#f3f3f3" : "#fff")};
  color: ${({ disabled, active }) => (active ? "#fff" : disabled ? "#aaa" : "#333")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  font-weight: ${({ active }) => (active ? "700" : "500")};
`;
